/**
 * モーショングラフィックス生成の「AIモデル」(初稿のコードを書くモデル)。2026-10-16 利用者決定で Gemini 4 Argon を選べるようにした
 * (docs/features/gemini-4-argon-migration.md)。料金は定額で変わらない。採点モデルは仕上げ方で決まる (ダメ出しは初稿と同じモデル)。
 *
 * 選択は全経路 (ブランドカット・レイヤー素材・シーン・全編) で共通の 1 つを localStorage に覚える (購読型のストア)。
 * 値は backend の許可リスト MOTION_SELECTABLE_MODELS (brand_cut_motion/loop.py) と揃える。
 */
import { useCallback, useSyncExternalStore } from 'react';

export type MotionModel = 'claude-opus-5-5' | 'gemini-4-argon';

export const MOTION_MODELS: readonly MotionModel[] = ['claude-opus-5-5', 'gemini-4-argon'];
export const DEFAULT_MOTION_MODEL: MotionModel = 'claude-opus-5-5';

export const MOTION_MODEL_STORAGE_KEY = 'motion_generation_model';

export const MOTION_MODEL_LABELS: Record<MotionModel, string> = {
  'claude-opus-5-5': 'Claude Opus 5.5',
  'gemini-4-argon': 'Gemini 4 Argon',
};
/** ドロップダウンの中で推奨バッジを付けるモデル (2026-10-16 利用者指示。閉じたときのボタンには出さない) */
export const RECOMMENDED_MOTION_MODEL: MotionModel = 'claude-opus-5-5';
/** 選択の見出し */
export const MOTION_MODEL_HEADING = 'AIモデル';

function isMotionModel(value: unknown): value is MotionModel {
  return typeof value === 'string' && (MOTION_MODELS as readonly string[]).includes(value);
}

const listeners = new Set<() => void>();
// localStorage が使えない環境で、その場の選択だけは保つための控え
let memoryFallback: MotionModel | null = null;

export function readMotionModel(): MotionModel {
  try {
    const value = localStorage.getItem(MOTION_MODEL_STORAGE_KEY);
    return isMotionModel(value) ? value : DEFAULT_MOTION_MODEL;
  } catch {
    return DEFAULT_MOTION_MODEL;
  }
}

export function writeMotionModel(model: MotionModel): void {
  try {
    localStorage.setItem(MOTION_MODEL_STORAGE_KEY, model);
    memoryFallback = null;
  } catch {
    /* 保存できなくても (プライベートモード等) 画面の状態は変える */
    memoryFallback = model;
  }
  for (const l of listeners) l();
}

function snapshot(): MotionModel {
  return memoryFallback ?? readMotionModel();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** AIモデルの選択の状態と setter (全経路で共有)。 */
export function useMotionModel(): [MotionModel, (next: MotionModel) => void] {
  const value = useSyncExternalStore(subscribe, snapshot, () => DEFAULT_MOTION_MODEL);
  const set = useCallback((next: MotionModel) => writeMotionModel(next), []);
  return [value, set];
}

/** 生成を送る瞬間の選択 (本文の model に入れる)。 */
export function currentMotionModel(): MotionModel {
  return snapshot();
}

/** テスト用: 控えを初期化する */
export function __resetMotionModelForTest(): void {
  memoryFallback = null;
}
